import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import i18n, { LOCALE_STORAGE_KEY, normalizeLocale, type Locale } from "@/i18n";
import { startDomTranslation, stopDomTranslation } from "@/i18n/domTranslate";

interface LanguageContextType {
  locale: Locale;
  setLocale: (locale: Locale) => Promise<void>;
  saving: boolean;
}

const LanguageContext = createContext<LanguageContextType>({
  locale: "en",
  setLocale: async () => {},
  saving: false,
});

export const useLanguage = () => useContext(LanguageContext);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const { user, profileId } = useAuth();
  const [locale, setLocaleState] = useState<Locale>(() =>
    normalizeLocale(localStorage.getItem(LOCALE_STORAGE_KEY) || i18n.resolvedLanguage || navigator.language),
  );
  const [saving, setSaving] = useState(false);

  // Apply language to i18next + the DOM translation layer.
  useEffect(() => {
    if (normalizeLocale(i18n.language) !== locale) i18n.changeLanguage(locale);
    document.documentElement.lang = locale;
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    startDomTranslation(locale);
    return () => stopDomTranslation();
  }, [locale]);

  // Load the user's stored preference once their profile is known.
  useEffect(() => {
    if (!user || !profileId) return;
    let cancelled = false;
    supabase
      .from("profiles")
      .select("locale")
      .eq("id", profileId)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled || !data?.locale) return;
        setLocaleState(normalizeLocale(data.locale));
      });
    return () => {
      cancelled = true;
    };
  }, [user, profileId]);

  const setLocale = useCallback(
    async (next: Locale) => {
      setLocaleState(next);
      if (!profileId) return;
      setSaving(true);
      try {
        await supabase.from("profiles").update({ locale: next }).eq("id", profileId);
      } finally {
        setSaving(false);
      }
    },
    [profileId],
  );

  return (
    <LanguageContext.Provider value={{ locale, setLocale, saving }}>{children}</LanguageContext.Provider>
  );
};
